import React from "react";
import { Tag } from "primereact/tag";
import type { ThgCreditNote, ThgStationRow } from "../../../types/thgAnnualSummary";
import { formatBerlin, formatBerlinDate, formatEur } from "../utils/thgFormat";
import { payoutReasonLabel, payoutStateLabel } from "../utils/thgPayout";
import { COCHARGE_REVENUE_TEXTS } from "../../StationPerformance/StationPerformance.consts";
import { PAYOUT_COPY } from "../ThgAnnualSummary.consts";
import { isUnpaidInternal } from "../utils/thgInternal";

type TagSeverity = "success" | "info" | "warning" | "danger" | null;

const severityOf = (row: ThgStationRow): TagSeverity => {
  const payout = row.payout;
  if (!payout) return null;
  if (payout.creating) return "info";
  switch (payout.state) {
    case "FINAL":
      return "success";
    case "DRAFT":
      return "warning";
    case "CANCELLED":
      return "danger";
    default:
      return null;
  }
};

const amounts = (note: ThgCreditNote) => PAYOUT_COPY.totalsNetGross(formatEur(note.netEur), formatEur(note.grossEur));

/** One line per earlier (cancelled) credit note: number, amounts and when it was generated. */
const earlierLine = (note: ThgCreditNote) =>
  `${note.number ?? "Draft"} · ${amounts(note)} · generated ${formatBerlinDate(note.generatedAt)}`;

const EarlierCreditNotes: React.FC<{ notes: ThgCreditNote[] }> = ({ notes }) => (
  <div className="text-sm text-color-secondary">
    <div>{PAYOUT_COPY.earlierCreditNotes(notes.length)}:</div>
    {notes.map((earlier) => (
      <div key={earlier.documentId} className="white-space-nowrap">
        {earlierLine(earlier)}
      </div>
    ))}
  </div>
);

interface PayoutStatusCellProps {
  row: ThgStationRow;
}

/**
 * Where a station's THG payout stands (CLR-283): the state, and for a credit note its number, dates and
 * amounts. Mismatches are always-visible warnings, never hover-only: on a payout list a hidden warning is a
 * missed one.
 */
const PayoutStatusCell: React.FC<PayoutStatusCellProps> = ({ row }) => {
  const payout = row.payout;
  if (!payout) return null;
  const previous = payout.previousCreditNotes ?? [];
  if (isUnpaidInternal(row)) {
    // Not paid, but the audit trail stays: a cancelled note still attached and any earlier notes.
    const earlier = payout.creditNote ? [payout.creditNote, ...previous] : previous;
    return (
      <div className="thg-payout-status flex flex-column gap-1" data-testid={`thg-payout-${row.stationId}`}>
        <Tag value={COCHARGE_REVENUE_TEXTS.internalBadge} className="thg-tag-neutral" />
        {earlier.length > 0 && <EarlierCreditNotes notes={earlier} />}
      </div>
    );
  }
  const reason = payoutReasonLabel(payout.state);
  const severity = severityOf(row);
  const note = payout.creating ? null : payout.creditNote;

  return (
    <div className="thg-payout-status flex flex-column gap-1" data-testid={`thg-payout-${row.stationId}`}>
      <span className="flex align-items-center gap-2 white-space-nowrap">
        <Tag
          value={payoutStateLabel(payout)}
          severity={severity}
          className={severity ? undefined : "thg-tag-neutral"}
          title={payout.statusCheckedAt ? PAYOUT_COPY.statusCheckedAt(formatBerlin(payout.statusCheckedAt)) : undefined}
        />
        {reason && <span className="text-sm">{reason}</span>}
      </span>

      {note && (
        <div className="flex flex-column text-sm text-color-secondary white-space-nowrap">
          <span className={note.number ? "font-semibold text-color" : undefined}>
            {note.number ?? (payout.state === "DRAFT" ? PAYOUT_COPY.noNumberYet : "No number")}
          </span>
          <span title={`by ${note.generatedBy}`}>Generated {formatBerlinDate(note.generatedAt)}</span>
          {note.finalizedAt && (
            <span title={note.finalizedBy ? `by ${note.finalizedBy}` : undefined}>
              Finalised {formatBerlinDate(note.finalizedAt)}
            </span>
          )}
          <span>{amounts(note)}</span>
        </div>
      )}

      {payout.amountMismatch && (
        <div className="thg-payout-flag text-sm">
          <span className="font-semibold">
            <i className="pi pi-exclamation-triangle mr-1" aria-hidden="true" />
            {PAYOUT_COPY.amountMismatch}
          </span>
          <div>{PAYOUT_COPY.amountMismatchDetail}</div>
        </div>
      )}
      {payout.recipientMismatch && (
        <div className="thg-payout-flag text-sm font-semibold">
          <i className="pi pi-exclamation-triangle mr-1" aria-hidden="true" />
          {PAYOUT_COPY.recipientMismatch(note?.recipientName ?? PAYOUT_COPY.unknownRecipient)}
        </div>
      )}

      {previous.length > 0 && <EarlierCreditNotes notes={previous} />}
    </div>
  );
};

export default PayoutStatusCell;
